import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import { NetworkAPI } from "apis";
import ModalWrapper from "components/common/ModalWrapper";
import ModalFooter from "components/common/ModalFooter";
import PromptModal from "components/common/PromptModal";
import DateTimeFormatter from "components/common/DateTimeFormatter";

import { MESSAGE } from "constants/Message";
import { getMessageTextColorClass } from "helpers/StyleClass";

const PROCESS_LIMIT = 5;

class NetworkEnsureResourcesModal extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isComplete: false,
      resultMsg: null,
      isModalOpen: false,

      isPromptModalOpen: false,
      promptHeader: null,
      promptMessage: null,
      promptAction: null,
      promptMsg: [],
    };

    this.isReachedProcessLimit = this.isReachedProcessLimit.bind(this);
    this.handleParseProcessResult = this.handleParseProcessResult.bind(this);
    this.handleEnsureResources = this.handleEnsureResources.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleModelConfirm = this.handleModelConfirm.bind(this);
  }

  componentDidMount() {
    const {
      process = {},
      handleClose,
      networkId,
      backgroundProcess = false,
    } = this.props;

    // Scenario #1: For Custom UPR
    if (!backgroundProcess) {
      this.handleEnsureResources();
      return;
    }

    const processData = _.get(process, networkId);

    // Scenario #2: Block due to has reached process limit
    // Current network process not in processObj but reached process limitation
    if (_.isNil(processData) && this.isReachedProcessLimit()) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Ensure Resources Limit Reached",
        promptMessage: (
          <>
            There are already{" "}
            <span className="font-semibold text-blue-600">{PROCESS_LIMIT}</span>{" "}
            ongoing ensure resources processes. <br />
            Please wait for them to complete before starting a new one.
          </>
        ),
        promptMsg: ["", MESSAGE.SUCCESS],
        promptAction: () => {
          if (handleClose) {
            handleClose();
          }
        },
      });

      return;
    }

    // Scenario #3: Processing or completed [success / error]
    // Current network in processObj
    if (!_.isNil(processData)) {
      const data = _.get(processData, "data");
      this.handleParseProcessResult(data);
    } else {
      this.handleEnsureResources();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !_.isNil(prevProps.process) &&
      !_.isNil(this.props.process) &&
      prevProps.process[this.props.networkId] !==
        this.props.process[this.props.networkId]
    ) {
      const prevProcessData = _.get(prevProps.process, [
        this.props.networkId,
        "data",
      ]);
      const currentProcessData = _.get(this.props.process, [
        this.props.networkId,
        "data",
      ]);

      // Processing to completed (with result)
      if (_.isNil(prevProcessData) && !_.isNil(currentProcessData)) {
        this.handleParseProcessResult(currentProcessData);
      }
    }
  }

  isReachedProcessLimit() {
    const { process = {} } = this.props;

    // "data" field is null means the process is still in progress.
    return (
      _.values(process).filter(({ data }) => _.isNil(data)).length >=
      PROCESS_LIMIT
    );
  }

  async handleEnsureResources(isPrompt = true) {
    const {
      networkId,
      name,
      handleProcess,
      backgroundProcess = false,
    } = this.props;
    if (!networkId) return;

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Ensure Resources",
        promptMessage: (
          <>
            Are you sure you want to ensure resources for network? <br />
            <span className="font-semibold text-blue-600">
              {networkId} {name}
            </span>
          </>
        ),
        promptMsg: [],
        promptAction: () => this.handleEnsureResources(false),
      });
      return;
    }

    // For background process, check process limit before executing.
    if (backgroundProcess && this.isReachedProcessLimit()) {
      this.setState({
        resultMsg: [
          "Process limit reached, please wait for ongoing processes to complete before starting a new one.",
          MESSAGE.ERROR,
        ],
        isLoading: false,
        isModalOpen: true,
        isPromptModalOpen: false,
      });
      return;
    }

    this.setState({
      isLoading: true,
      isModalOpen: true,
      isPromptModalOpen: false,
    });

    if (backgroundProcess && handleProcess) {
      handleProcess({
        execfunc: async () => {
          return await NetworkAPI.triggerEnsureResources({ networkId });
        },
        networkId,
        name,
      });
      return;
    }

    try {
      const { networkId } = this.props;
      const r = await NetworkAPI.triggerEnsureResources({ networkId });

      this.setState({
        isComplete: (r.result || "") === "OK",
        resultMsg: [JSON.stringify(r, null, 2), MESSAGE.SUCCESS],
        isLoading: false,
      });
    } catch (err) {
      this.setState({
        isComplete: false,
        resultMsg: [err.toString(), MESSAGE.ERROR],
        isLoading: false,
      });
    }
  }

  handleModelConfirm() {
    const { isComplete } = this.state;
    if (isComplete) {
      this.handleModalClose();
    } else {
      this.handleEnsureResources(false);
    }
  }

  handleModalClose() {
    this.setState(
      {
        isModalOpen: false,
        isLoading: false,
        resultMsg: null,
      },
      () => {
        if (this.props.handleClose) {
          this.props.handleClose();
        }
      }
    );
  }

  handleParseProcessResult(data) {
    // Processing
    if (_.isNil(data)) {
      this.setState({
        isLoading: true,
        isModalOpen: true,
        isPromptModalOpen: false,
      });
      return;
    }

    // Completed with result
    if (data.result === "OK") {
      this.setState({
        isComplete: true,
        isModalOpen: true,
        resultMsg: [JSON.stringify(data, null, 2), MESSAGE.SUCCESS],
        isLoading: false,
      });
    } else {
      this.setState({
        isComplete: false,
        isModalOpen: true,
        resultMsg: [data.error || "Unknown error", MESSAGE.ERROR],
        isLoading: false,
      });
    }
  }

  render() {
    const { handleClose, handleProcess, networkId, name } = this.props;
    const {
      isComplete,
      isLoading,
      resultMsg,
      isModalOpen,

      isPromptModalOpen,
      promptHeader,
      promptMessage,
      promptAction,
      promptMsg,
    } = this.state;

    const [content, status] = resultMsg || [];

    const parsedIsLoading = handleProcess ? false : isLoading;
    const parsedIsComplete = (handleProcess && isLoading) || isComplete;

    return (
      <>
        {isModalOpen && (
          <ModalWrapper
            isOpen={isModalOpen}
            customOverlayStyle={{ zIndex: 100 }}
            title={{
              label: "Ensure Resources ",
              highlight: `${networkId || ""} - ${name || ""}`,
            }}
          >
            <label className="text-gray-800">Current Time</label>
            <div className="mb-2">
              <DateTimeFormatter
                datetime={moment().toISOString()}
                timezone={moment.tz.guess()}
                timeOnly={true}
              ></DateTimeFormatter>
            </div>

            <label className="text-gray-800">Result</label>
            <div
              className="rounded border mb-8 w-full overflow-y-auto border-gray-400 bg-gray-100"
              style={{ minHeight: "100px", maxHeight: "300px" }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center pt-4">
                  Processing ...
                </div>
              ) : (
                <pre
                  className={`px-2 py-1 ${getMessageTextColorClass(status)}`}
                >
                  {content}
                </pre>
              )}
            </div>
            <ModalFooter
              isLoading={parsedIsLoading}
              msg={parsedIsComplete ? ["", MESSAGE.SUCCESS] : []}
              confirmText="Execute Again"
              onCancel={this.handleModalClose}
              onConfirm={this.handleModelConfirm}
            />
          </ModalWrapper>
        )}

        {isPromptModalOpen && (
          <PromptModal
            isLoading={isLoading}
            isOpenModal={isPromptModalOpen}
            msg={promptMsg}
            handleConfirm={promptAction}
            handleCancel={() => {
              this.setState({ isPromptModalOpen: false });
              if (handleClose) {
                handleClose();
              }
            }}
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}
      </>
    );
  }
}

export default NetworkEnsureResourcesModal;
